//TODO: recently moved into nav plugin. needs further integration
import { Application, Container } from '../..';

import { Lock } from '../../../lib/pattern/Lock';
import { BasicButton } from '../../../lib/ui/BasicButton';
import { numberInRange } from '../../../lib/util/mathTools';
import { IScreen } from './IScreen';
import { IScreenController } from './IScreenController';

// types
//-----------------------------------------------------------------------------
// public
export type TabScreenOptions = {
  width: number;
  height: number;
};

export type TabScreenClass = new (options: TabScreenOptions) => IScreen;
export type TabControllerClass = new (screen: IScreen) => IScreenController;
export type TabDef = {
  button: BasicButton;
  screen: TabScreenClass;
  controller?: TabControllerClass;
  options?: Record<string, unknown>;
};

export type TabNavOptions = {
  app: Application;
  // container must be flex'ed
  container: Container;
  tabs: TabDef[];
};

export type TabState = {
  button: BasicButton;
  screen: IScreen;
  controller?: IScreenController;
  options?: Record<string, unknown>;
};

/*
    controls tabs and content
*/
export class TabNav {
  // fields
  //-------------------------------------------------------------------------
  // events
  public onOpen?: (tab: number) => void;
  public onClose?: (tab: number) => void;
  // input
  private _app: Application;
  private readonly _container: Container;
  // state
  private readonly _tabs: TabState[] = [];
  private _activeIndex = -1;
  private _tabLock = new Lock();

  // properties
  //-------------------------------------------------------------------------
  public get tab(): number {
    return this._activeIndex;
  }

  public get tabs(): TabState[] {
    return this._tabs;
  }

  // init
  //-------------------------------------------------------------------------
  constructor(options: TabNavOptions) {
    // set fields
    this._app = options.app;
    this._container = options.container;

    // create tab states
    this._tabs = options.tabs.map((def, index) => {
      // register events
      def.button.interactive = true;
      def.button.setOnTap(() => this._onButtonPress(index));

      // create screen
      const screen = new def.screen({
        width: this._container.width,
        height: this._container.height,
      });

      // create controller
      let controller: IScreenController;
      if (def.controller) {
        controller = new def.controller(screen);
      }

      // create tab state
      return {
        button: def.button,
        screen,
        controller: controller!,
        options: def.options,
      };
    });
  }

  // api
  //-------------------------------------------------------------------------
  public async setTab(index: number, options?: Record<string, unknown>) {
    // if changing
    if (
      index !== this._activeIndex &&
      this._isTabValid(index) &&
      this._tabLock.lock()
    ) {
      // close active tab if exists
      if (this._isTabValid(this._activeIndex))
        this._closeTab(this._activeIndex);

      // update active index
      this._activeIndex = index;

      // open new tab
      await this._openTab(index, options);

      // unlock
      this._tabLock.unlock();
    }
  }

  public async reset() {
    // if current valid
    if (this._isTabValid(this._activeIndex)) {
      // close active tab
      this._closeTab(this._activeIndex);

      // reset active index
      this._activeIndex = -1;
    }
  }

  // private: events
  //-------------------------------------------------------------------------
  private _onButtonPress(index: number) {
    // set new tab
    this.setTab(index);
  }

  // private: core
  //-------------------------------------------------------------------------
  private async _openTab(index: number, options?: Record<string, unknown>) {
    // const nav = this._app.get(NavPlugin);

    // get tab state
    const tab = this._tabs[index];

    // generate final options
    options = { ...options, ...tab.options };

    //TODO: this may not work well if nav plugin is also loading. instead we'll want a queued version like NavPlugin.loader()
    // run loader for the given tab screen's assets
    // await nav.loaderManager.loadAssets(tab.screen.assets?.(options) || [], nav.defaultLoader, true);

    // add tab content to container
    this._container.addChild(tab.screen.view);

    // raise tab button above container
    // tab.button.zIndex = this._container.zIndex + 1;
    tab.button.parent!.sortChildren();

    // set button selected
    tab.button.selected = true;

    // prepare controller and tab
    await tab.screen.prepare?.(options);
    await tab.controller?.prepare?.(options);

    // notify
    this.onOpen?.(index);
  }

  private _closeTab(index: number) {
    // get tab state
    const tab = this._tabs[index];

    // notify
    this.onClose?.(index);

    // hidden tab and controller
    tab.controller?.hidden?.();
    tab.screen.hidden?.();

    // set button unselected
    tab.button.selected = false;

    // lower tab button below container
    // tab.button.zIndex = this._container.zIndex - 1;
    tab.button.parent!.sortChildren();

    // remove tab content from container
    this._container.removeChild(tab.screen.view);
  }

  // private: support
  //-------------------------------------------------------------------------
  private _isTabValid(index: number) {
    return numberInRange(index, 0, this._tabs.length - 1);
  }
}
